/**
 * ScanCapturePage — mobile-first camera capture page for shop scanning.
 *
 * Flow:
 *   1. Enter shop/section name
 *   2. Start camera preview
 *   3. Record (auto-capture at 2fps)
 *   4. Review frames
 *   5. Upload → track progress → done
 */

import { useState, useMemo, useRef, useCallback } from 'react'
import { useCamera } from '@hooks/useCamera'
import { useUploader } from '@hooks/useUploader'
import type { ScanStatus } from '@app-types/scan'
import './ScanCapturePage.css'

type PageStep = 'setup' | 'camera' | 'review' | 'upload'

export default function ScanCapturePage() {
  const [step, setStep] = useState<PageStep>('setup')

  // Read shop/section from URL params (set by ShopsPage "Scan" button)
  const urlParams = new URLSearchParams(window.location.search)
  const [shopId, setShopId] = useState(urlParams.get('shopId') || '')
  const [sectionName, setSectionName] = useState(urlParams.get('sectionName') || '')
  const sectionIdFromUrl = urlParams.get('sectionId') || ''

  const [previewIndex, setPreviewIndex] = useState<number | null>(null)

  const camera = useCamera({ targetFps: 2, quality: 0.85, maxDimension: 800 })
  const uploader = useUploader()

  // Frame blobs for upload
  const frameBlobs = useMemo(() => camera.frames.map((f) => f.blob), [camera.frames])

  // ── Setup Step ──
  const handleStartCamera = () => {
    if (!shopId.trim() || !sectionName.trim()) return
    // Switch to camera step FIRST so the <video> element mounts,
    // then startCamera will attach the stream via effect.
    setStep('camera')
    camera.startCamera()
  }

  // ── Camera Step ──
  const handleStartRecording = () => {
    camera.startCapture()
  }

  const handleStopRecording = () => {
    camera.stopCapture()
    if (camera.frames.length > 0) {
      setStep('review')
    }
  }

  // ── Review Step ──
  const handleRetake = () => {
    camera.clearFrames()
    setStep('camera')
  }

  const handleUpload = async () => {
    setStep('upload')
    camera.stopCamera() // Free camera while uploading
    await uploader.upload(frameBlobs, shopId, sectionName, sectionIdFromUrl || undefined)
  }

  // ── Upload Step ──
  const handleNewScan = () => {
    uploader.reset()
    camera.clearFrames()
    setStep('setup')
  }

  return (
    <div className="scan-page">
      <header className="scan-header">
        <h1>Dukkan Scan</h1>
        {step !== 'setup' && (
          <button className="scan-btn-text" onClick={() => { camera.stopCamera(); camera.stopCapture(); setStep('setup') }}>
            Back
          </button>
        )}
      </header>

      {/* ── SETUP ── */}
      {step === 'setup' && (
        <div className="scan-step scan-setup">
          <div className="scan-field">
            <label>Shop ID</label>
            <input
              type="text"
              placeholder="e.g. my_coffee_shop"
              value={shopId}
              onChange={(e) => setShopId(e.target.value)}
            />
          </div>
          <div className="scan-field">
            <label>Section Name</label>
            <input
              type="text"
              placeholder="e.g. Entrance, Main Hall"
              value={sectionName}
              onChange={(e) => setSectionName(e.target.value)}
            />
          </div>
          <button
            className="scan-btn-primary"
            disabled={!shopId.trim() || !sectionName.trim()}
            onClick={handleStartCamera}
          >
            Start Camera
          </button>
        </div>
      )}

      {/* ── CAMERA ── */}
      {step === 'camera' && (
        <div className="scan-step scan-camera">
          <div className="scan-video-container">
            <video ref={camera.videoRef} autoPlay playsInline muted className="scan-video" />
            <canvas ref={camera.canvasRef} style={{ display: 'none' }} />
            {camera.isCapturing && (
              <div className="scan-recording-badge">
                <span className="scan-dot" /> REC &middot; {camera.frames.length} frames
              </div>
            )}
          </div>

          {camera.error && <div className="scan-error">{camera.error}</div>}

          <div className="scan-controls">
            {!camera.isCapturing ? (
              <button className="scan-btn-record" onClick={handleStartRecording} disabled={!camera.isStreaming}>
                <span className="scan-record-circle" />
                Start Recording
              </button>
            ) : (
              <button className="scan-btn-stop" onClick={handleStopRecording}>
                <span className="scan-stop-square" />
                Stop ({camera.frames.length})
              </button>
            )}
          </div>

          <div className="scan-tip">
            Walk slowly through the space. Keep the camera steady.
          </div>
        </div>
      )}

      {/* ── REVIEW ── */}
      {step === 'review' && (
        <div className="scan-step scan-review">
          <div className="scan-summary">
            <h2>{camera.frames.length} Frames Captured</h2>
            <p>{sectionName} &middot; {shopId}</p>
            <p className="scan-tap-hint">Tap a frame to preview &amp; delete</p>
          </div>

          <div className="scan-frame-strip">
            {camera.frames.map((frame, i) => (
              <img
                key={frame.index}
                src={URL.createObjectURL(frame.blob)}
                alt={`Frame ${i + 1}`}
                className="scan-thumb"
                onClick={() => setPreviewIndex(i)}
              />
            ))}
          </div>

          <div className="scan-review-actions">
            <button className="scan-btn-secondary" onClick={handleRetake}>
              Retake
            </button>
            <button className="scan-btn-primary" onClick={handleUpload} disabled={camera.frames.length === 0}>
              Upload ({camera.frames.length} frames)
            </button>
          </div>

          {/* ── Frame Preview Overlay ── */}
          {previewIndex !== null && previewIndex < camera.frames.length && (
            <FramePreview
              frames={camera.frames}
              currentIndex={previewIndex}
              onNavigate={setPreviewIndex}
              onDelete={(idx) => {
                camera.removeFrame(idx)
                if (camera.frames.length <= 1) {
                  setPreviewIndex(null)
                } else if (idx >= camera.frames.length - 1) {
                  setPreviewIndex(idx - 1)
                }
              }}
              onClose={() => setPreviewIndex(null)}
            />
          )}
        </div>
      )}

      {/* ── UPLOAD / PROGRESS ── */}
      {step === 'upload' && (
        <div className="scan-step scan-upload">
          <StatusView phase={uploader.phase} progress={uploader.progress} scan={uploader.scan} error={uploader.error} />

          {(uploader.phase === 'done' || uploader.phase === 'error') && (
            <button className="scan-btn-primary" onClick={handleNewScan} style={{ marginTop: 24 }}>
              New Scan
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Frame Preview with swipe ──
function FramePreview({
  frames,
  currentIndex,
  onNavigate,
  onDelete,
  onClose,
}: {
  frames: { blob: Blob; index: number }[]
  currentIndex: number
  onNavigate: (idx: number) => void
  onDelete: (idx: number) => void
  onClose: () => void
}) {
  const touchStartX = useRef(0)
  const touchDeltaX = useRef(0)
  const stripRef = useRef<HTMLDivElement>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchDeltaX.current = 0
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current
    // Apply live translate for feedback
    if (stripRef.current) {
      stripRef.current.style.transform = `translateX(${touchDeltaX.current}px)`
      stripRef.current.style.transition = 'none'
    }
  }, [])

  const handleTouchEnd = useCallback(() => {
    const delta = touchDeltaX.current
    const threshold = 50 // minimum px to count as swipe
    if (stripRef.current) {
      stripRef.current.style.transform = ''
      stripRef.current.style.transition = 'transform 0.2s ease'
    }
    if (delta < -threshold && currentIndex < frames.length - 1) {
      onNavigate(currentIndex + 1) // swipe left → next
    } else if (delta > threshold && currentIndex > 0) {
      onNavigate(currentIndex - 1) // swipe right → prev
    }
    touchDeltaX.current = 0
  }, [currentIndex, frames.length, onNavigate])

  return (
    <div className="scan-preview-overlay" onClick={onClose}>
      <div className="scan-preview-content" onClick={(e) => e.stopPropagation()}>
        <div className="scan-preview-header">
          <span className="scan-preview-counter">
            {currentIndex + 1} / {frames.length}
          </span>
          <button className="scan-preview-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div
          className="scan-preview-image-wrap"
          ref={stripRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {currentIndex > 0 && (
            <button className="scan-preview-nav scan-preview-prev" onClick={() => onNavigate(currentIndex - 1)}>
              ‹
            </button>
          )}
          <img
            src={URL.createObjectURL(frames[currentIndex].blob)}
            alt={`Frame ${currentIndex + 1}`}
            className="scan-preview-img"
            draggable={false}
          />
          {currentIndex < frames.length - 1 && (
            <button className="scan-preview-nav scan-preview-next" onClick={() => onNavigate(currentIndex + 1)}>
              ›
            </button>
          )}
        </div>

        <div className="scan-preview-actions">
          <button className="scan-btn-delete" onClick={() => onDelete(currentIndex)}>
            🗑 Delete Frame
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Status sub-component ──
function StatusView({
  phase,
  progress,
  scan,
  error,
}: {
  phase: string
  progress: { total: number; uploaded: number; failed: number; percent: number }
  scan: { id: string; status: ScanStatus; processingTime?: string; datasetId?: string } | null
  error: string | null
}) {
  if (error) {
    return (
      <div className="scan-status scan-status-error">
        <div className="scan-status-icon">✗</div>
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    )
  }

  if (phase === 'creating') {
    return (
      <div className="scan-status">
        <div className="scan-spinner" />
        <h2>Creating scan session...</h2>
      </div>
    )
  }

  if (phase === 'uploading') {
    return (
      <div className="scan-status">
        <div className="scan-progress-bar">
          <div className="scan-progress-fill" style={{ width: `${progress.percent}%` }} />
        </div>
        <h2>Uploading frames</h2>
        <p>
          {progress.uploaded} / {progress.total}
          {progress.failed > 0 && <span className="scan-failed"> ({progress.failed} failed)</span>}
        </p>
      </div>
    )
  }

  if (phase === 'completing') {
    return (
      <div className="scan-status">
        <div className="scan-spinner" />
        <h2>Finalizing upload...</h2>
      </div>
    )
  }

  if (phase === 'polling' && scan) {
    return (
      <div className="scan-status">
        <div className="scan-spinner" />
        <h2>Processing with COLMAP</h2>
        <p>Status: {scan.status}</p>
        <p className="scan-hint">This may take a few minutes. You can close this page — processing continues on the server.</p>
      </div>
    )
  }

  if (phase === 'done' && scan) {
    const isReady = scan.status === 'ready'
    return (
      <div className={`scan-status ${isReady ? 'scan-status-success' : 'scan-status-error'}`}>
        <div className="scan-status-icon">{isReady ? '✓' : '✗'}</div>
        <h2>{isReady ? 'Scan Complete!' : 'Processing Failed'}</h2>
        {scan.processingTime && <p>Processing time: {scan.processingTime}</p>}
        <p className="scan-scan-id">Scan ID: {scan.id}</p>
        {isReady && scan.datasetId && (
          <a
            href={`/?shopid=${encodeURIComponent(scan.datasetId)}`}
            className="scan-btn-view-shop"
          >
            View Shop →
          </a>
        )}
      </div>
    )
  }

  return null
}
